import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  getServiceRequest,
  getServiceRequests,
  markServiceRequestSeen,
  rejectServiceRequest,
  type ServiceRequestDto,
} from '@/api/service-request.api'
import { resetStore } from '@/store/actions/reset'
import { createServiceOrderThunk } from '@/store/slices/service-orders-slice'
import type { RootState } from '@/store/store'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

interface ServiceRequestsState {
  requests: ServiceRequestDto[]
  status: Status
  current: ServiceRequestDto | null
  currentStatus: Status
  currentRequestId: string | null
  refreshRequestId: string | null
}

const initialState: ServiceRequestsState = {
  requests: [],
  status: 'idle',
  current: null,
  currentStatus: 'idle',
  currentRequestId: null,
  refreshRequestId: null,
}

export const fetchServiceRequests = createAsyncThunk<ServiceRequestDto[], void, { state: RootState }>(
  'serviceRequests/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceRequests()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
  // Licznik w zakładkach modułu i widok listy montują się razem — trwające pobranie wystarczy obu.
  { condition: (_, { getState }) => getState().serviceRequests.status !== 'loading' },
)

// Odświeżenie licznika w tle: nieudane nie przełącza widoku na błąd i zostawia ostatnią listę.
// Jedno naraz; zmiana listy po stronie kierownika unieważnia trwające, żeby stara odpowiedź jej nie cofnęła.
export const refreshServiceRequests = createAsyncThunk<ServiceRequestDto[], void, { state: RootState }>(
  'serviceRequests/refresh',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceRequests()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
  {
    condition: (_, { getState }) => {
      const { status, refreshRequestId } = getState().serviceRequests
      return status !== 'loading' && refreshRequestId === null
    },
  },
)

export const fetchServiceRequest = createAsyncThunk<ServiceRequestDto, string>(
  'serviceRequests/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      return await getServiceRequest(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const markServiceRequestSeenThunk = createAsyncThunk<string, string>(
  'serviceRequests/seen',
  async (id, { rejectWithValue }) => {
    try {
      await markServiceRequestSeen(id)
      return id
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const rejectServiceRequestThunk = createAsyncThunk<string, string>(
  'serviceRequests/reject',
  async (id, { rejectWithValue }) => {
    try {
      await rejectServiceRequest(id)
      return id
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const serviceRequestsSlice = createSlice({
  name: 'serviceRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceRequests.pending, (state) => {
        state.status = 'loading'
        state.refreshRequestId = null
      })
      .addCase(fetchServiceRequests.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.requests = action.payload
      })
      .addCase(fetchServiceRequests.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(refreshServiceRequests.pending, (state, action) => {
        state.refreshRequestId = action.meta.requestId
      })
      .addCase(refreshServiceRequests.fulfilled, (state, action) => {
        if (state.refreshRequestId !== action.meta.requestId) return
        state.refreshRequestId = null
        state.status = 'succeeded'
        state.requests = action.payload
      })
      .addCase(refreshServiceRequests.rejected, (state, action) => {
        if (state.refreshRequestId === action.meta.requestId) state.refreshRequestId = null
      })
      .addCase(fetchServiceRequest.pending, (state, action) => {
        state.currentRequestId = action.meta.requestId
        state.currentStatus = 'loading'
        if (state.current?.id !== action.meta.arg) state.current = null
      })
      // Przejście między zgłoszeniami nie może pokazać zgłoszenia, którego odpowiedź wróciła później.
      .addCase(fetchServiceRequest.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.currentStatus = 'succeeded'
        state.current = action.payload
      })
      .addCase(fetchServiceRequest.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.currentStatus = 'failed'
        state.current = null
      })
      .addCase(markServiceRequestSeenThunk.fulfilled, (state, action) => {
        state.refreshRequestId = null
        const seenAt = new Date().toISOString()
        state.requests.forEach((request) => {
          if (request.id === action.payload && !request.seenAt) request.seenAt = seenAt
        })
        if (state.current?.id === action.payload && !state.current.seenAt) state.current.seenAt = seenAt
      })
      // Zamienione w zlecenie i odrzucone nie wracają na listę oczekujących.
      .addCase(rejectServiceRequestThunk.fulfilled, (state, action) => {
        state.refreshRequestId = null
        state.requests = state.requests.filter((request) => request.id !== action.payload)
        if (state.current?.id === action.payload) state.current = null
      })
      .addCase(createServiceOrderThunk.fulfilled, (state, action) => {
        const { serviceRequestId } = action.meta.arg
        if (!serviceRequestId) return
        state.refreshRequestId = null
        state.requests = state.requests.filter((request) => request.id !== serviceRequestId)
        if (state.current?.id === serviceRequestId) state.current = null
      })
      .addCase(resetStore, () => initialState)
  },
})

export default serviceRequestsSlice.reducer
